import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { readString, writeString, removeKey, STORAGE_KEYS } from "~/shared/lib/storage";

import { useUserWorkspacesDetailed } from "./api";

import { classifyWorkspace } from "./workspace-utils";
import { ActiveWorkspaceContext, type ActiveWorkspaceContextValue } from "./use-active-workspace";

const readStoredId = (): string | null => readString(STORAGE_KEYS.activeWorkspaceId);

const writeStoredId = (id: string | null): void =>
    id ? writeString(STORAGE_KEYS.activeWorkspaceId, id) : removeKey(STORAGE_KEYS.activeWorkspaceId);

export function ActiveWorkspaceProvider({ children }: { children: ReactNode }) {
    const { data, isLoading, isError } = useUserWorkspacesDetailed();
    const workspaces = useMemo(() => data ?? [], [data]);

    const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(() => readStoredId());

    useEffect(() => {
        if (workspaces.length === 0) return;
        const stillExists = activeWorkspaceId && workspaces.some((w) => w.workspace.id === activeWorkspaceId);
        if (stillExists) return;
        const personal = workspaces.find((w) => classifyWorkspace(w) === "personal");
        const fallback = personal?.workspace.id ?? workspaces[0]?.workspace.id ?? null;
        if (fallback && fallback !== activeWorkspaceId) {
            setActiveWorkspaceId(fallback);
            writeStoredId(fallback);
        }
    }, [workspaces, activeWorkspaceId]);

    const setActive = useCallback((workspaceId: string) => {
        setActiveWorkspaceId(workspaceId);
        writeStoredId(workspaceId);
    }, []);

    const activeWorkspace = useMemo(
        () => workspaces.find((w) => w.workspace.id === activeWorkspaceId) ?? null,
        [workspaces, activeWorkspaceId],
    );

    const value = useMemo<ActiveWorkspaceContextValue>(
        () => ({
            activeWorkspaceId,
            activeWorkspace,
            workspaces,
            setActive,
            isLoading,
            isError,
        }),
        [activeWorkspaceId, activeWorkspace, workspaces, setActive, isLoading, isError],
    );

    return <ActiveWorkspaceContext.Provider value={value}>{children}</ActiveWorkspaceContext.Provider>;
}
